/**
 * Search state — manages Bible text search with exact/any mode and pagination.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import type { BibleLocation, SearchResult } from '@skjv/common';
import { formatReference, parseBookScope, parseNavReference } from '@skjv/common';
import { performSearch } from './perform-search.js';

const PAGE_SIZE = 20;

export class SearchState {
  results: SearchResult[] = [];
  total = 0;
  searchText = '';
  highlightTerms = '';
  isExact = false;
  isSearching = false;
  hasMore = false;
  referenceMatch: BibleLocation | null = null;

  private searchCursor = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private currentRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  get referenceLabel(): string {
    return this.referenceMatch ? formatReference(this.referenceMatch) : '';
  }

  setSearchText(text: string): void {
    this.searchText = text;
    this.referenceMatch = parseNavReference(text);
    this.resetResults();
    this.debouncedSearch();
  }

  setIsExact(isExact: boolean): void {
    this.isExact = isExact;
    this.resetResults();
    if (this.searchText.trim()) {
      this.executeSearch();
    }
  }

  async loadMore(): Promise<void> {
    if (!this.hasMore || this.isSearching) return;
    this.searchCursor += PAGE_SIZE;
    await this.executeSearch(true);
  }

  clear(): void {
    this.searchText = '';
    this.referenceMatch = null;
    this.resetResults();
  }

  private resetResults(): void {
    this.results = [];
    this.total = 0;
    this.searchCursor = 0;
    this.hasMore = false;
  }

  private debouncedSearch(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (!this.searchText.trim()) return;

    this.debounceTimer = setTimeout(() => {
      this.executeSearch();
    }, 400);
  }

  private async executeSearch(isLoadMore = false): Promise<void> {
    const text = this.searchText.trim();
    if (!text) return;

    const requestId = ++this.currentRequestId;
    runInAction(() => { this.isSearching = true; });

    // Parse book scope: "cloud in Hebrews" → search "cloud" filtered to Hebrews
    const scope = parseBookScope(text);
    const searchText = scope ? scope.remainder : text;
    if (!searchText) return;

    runInAction(() => { this.highlightTerms = searchText; });

    const response = await performSearch({
      text: searchText,
      isExact: this.isExact,
      limit: PAGE_SIZE,
      offset: this.searchCursor,
      ...(scope && {
        testament: scope.bookInfo.testamentIndex + 1,
        bookNumber: scope.bookInfo.bookIndex + 1,
      }),
    });

    // Discard stale responses
    if (requestId !== this.currentRequestId) return;

    runInAction(() => {
      if (isLoadMore) {
        this.results = [...this.results, ...response.results];
      } else {
        this.results = response.results;
      }
      this.total = response.total;
      this.hasMore = response.hasMore;
      this.isSearching = false;
    });
  }
}
